import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ApprovalDialog } from '@/components/efs/ApprovalDialog';
import { PurgeMasterDialog } from '@/components/efs/PurgeMasterDialog';
import { useCurrentUser } from '@/lib/auth-context';
import { canApproveMasters, hasPermission } from '@/lib/permissions';
import { DestinationsFormDialog, type DestinationsDialogMode } from './DestinationsFormDialog';
import {
  useActivateDestination,
  useApproveDestination,
  usePurgeDestination,
  useRejectDestination,
  useRetireDestination,
} from './useDestinations';
import type { Destination } from './types';

interface DestinationsRowActionsProps {
  row: Destination;
}

export const DestinationsRowActions = ({ row }: DestinationsRowActionsProps) => {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const approve = useApproveDestination();
  const reject = useRejectDestination();
  const retire = useRetireDestination();
  const activate = useActivateDestination();
  const purge = usePurgeDestination();
  const [intent, setIntent] = useState<'approve' | 'reject' | null>(null);
  const [editMode, setEditMode] = useState<DestinationsDialogMode | null>(null);
  const [purgeOpen, setPurgeOpen] = useState(false);

  const canApprove = canApproveMasters(user);
  const canDirectEdit = hasPermission(user, 'master.approve');
  const canPropose = hasPermission(user, 'master.update');

  // P1-#3: DB approval_status uses {draft, pending_approval} for in-flight rows.
  const isPending = row.approval_status === 'pending_approval' || row.approval_status === 'draft';
  const isApproved = row.approval_status === 'approved';
  const isRetired = row.approval_status === 'retired';

  const showApprove = canApprove && isPending;
  const showReject = canApprove && isPending;
  const showDirect = canDirectEdit && isApproved;
  const showPropose = !canDirectEdit && canPropose && isApproved;
  const showRetire = canDirectEdit && isApproved;
  const showActivate = canDirectEdit && isRetired;
  const showPurge = canDirectEdit && isRetired;

  if (
    !showApprove &&
    !showReject &&
    !showDirect &&
    !showPropose &&
    !showRetire &&
    !showActivate &&
    !showPurge
  )
    return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('efs.masters.actions.row_menu', { defaultValue: 'アクション' })}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {showApprove ? (
            <DropdownMenuItem onSelect={() => setIntent('approve')}>
              {t('efs.masters.actions.approve', { ns: 'masters' })}
            </DropdownMenuItem>
          ) : null}
          {showReject ? (
            <DropdownMenuItem onSelect={() => setIntent('reject')}>
              {t('efs.masters.actions.reject', { ns: 'masters' })}
            </DropdownMenuItem>
          ) : null}
          {showDirect ? (
            <DropdownMenuItem onSelect={() => setEditMode('direct')}>
              {t('efs.masters.actions.edit_direct', { ns: 'masters' })}
            </DropdownMenuItem>
          ) : null}
          {showPropose ? (
            <DropdownMenuItem onSelect={() => setEditMode('proposal')}>
              {t('efs.masters.actions.propose_change', { ns: 'masters' })}
            </DropdownMenuItem>
          ) : null}
          {showRetire ? (
            <DropdownMenuItem
              onSelect={() => retire.mutate(row.destination_company_id)}
              disabled={retire.isPending}
            >
              {t('efs.masters.actions.deactivate', { ns: 'masters' })}
            </DropdownMenuItem>
          ) : null}
          {showActivate ? (
            <DropdownMenuItem
              onSelect={() => activate.mutate(row.destination_company_id)}
              disabled={activate.isPending}
            >
              {t('efs.masters.actions.activate', { ns: 'masters' })}
            </DropdownMenuItem>
          ) : null}
          {showPurge ? (
            <DropdownMenuItem
              onSelect={() => {
                purge.reset();
                setPurgeOpen(true);
              }}
              className="text-red-900 focus:text-red-900"
            >
              {t('efs.masters.actions.purge', { ns: 'masters' })}
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      <ApprovalDialog
        open={intent !== null}
        onOpenChange={(open) => {
          if (!open) setIntent(null);
        }}
        intent={intent ?? 'approve'}
        isSubmitting={approve.isPending || reject.isPending}
        requireReason={intent === 'reject'}
        onConfirm={async (reason) => {
          if (intent === 'approve') {
            await approve.mutateAsync({ id: row.destination_company_id, reason });
          } else if (intent === 'reject') {
            await reject.mutateAsync({ id: row.destination_company_id, reason });
          }
          setIntent(null);
        }}
      />

      <PurgeMasterDialog
        open={purgeOpen}
        onOpenChange={setPurgeOpen}
        name={row.company_display_name}
        isPending={purge.isPending}
        error={purge.error}
        onConfirm={() =>
          purge.mutate(row.destination_company_id, { onSuccess: () => setPurgeOpen(false) })
        }
      />

      <DestinationsFormDialog
        open={editMode !== null}
        onOpenChange={(open) => {
          if (!open) setEditMode(null);
        }}
        row={editMode !== null ? row : null}
        mode={editMode ?? 'direct'}
      />
    </>
  );
};
